import {useMemo, useState, useEffect} from 'react';
import {useSelector} from 'react-redux';

import {Illustration} from 'shells/illustrations/illustration';
import {Stack} from 'shells/layout/stack/stack';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import {Body} from 'shells/typography/body';

import {type FormPageFragment, PageRuleActionType} from 'nutshell-graphql-types';

import {getFormSchema} from '../../../../forms-selectors';
import type {EnumFieldOption} from '../../../../types';

import {useUpdateFormPageRules} from '../../graphql/hooks/use-update-form-page-rules';

import type {SidebarState} from '../sidebar';
import {SidebarPanelHeader} from '../sidebar-panel-header/sidebar-panel-header';

import {BranchingRow} from './branching-row';
import {buildNavOptions, rulesToNavValueByEnumElementId, parseRuleOptionId} from './helpers';

export interface SelectPickerOption {
    id: string;
    label: string;
}

interface Props {
    setSidebarState: (sidebarState: SidebarState) => void;
    graphqlPages?: FormPageFragment[] | null;
    activePageIndex: number | null | undefined;
}

export function BranchingRules(props: Props) {
    const [selectedFieldOption, setSelectedFieldOption] = useState<SelectPickerOption | null>(null);
    const [ruleByFieldOptionId, setRuleByFieldOptionId] = useState<
        Record<string, SelectPickerOption | null>
    >({});

    const {updateFormPageRules} = useUpdateFormPageRules();

    const formSchema = useSelector(getFormSchema); // Contains the enum options
    const activePage = props.graphqlPages?.[props.activePageIndex ?? 0]; // Contains the fields and conditional rules
    const activePageConditionalRules = rulesToNavValueByEnumElementId(activePage?.conditionalRules);

    // Map the fields to options for the select picker
    const filteredFields = useMemo(() => {
        return (
            activePage?.fields?.filter((field) => {
                if (field.attribute?.type === 'ENUM' && field.type !== 'CHECKBOXES') {
                    return true;
                }
                return false;
            }) || []
        );
    }, [activePage?.fields]);
    const fieldSelectorOptions: SelectPickerOption[] =
        filteredFields?.map((field) => {
            return {
                id: field.id,
                label: field.label,
            };
        }) || [];
    const selectedField = filteredFields.find((field) => field.id === selectedFieldOption?.id);

    // If there are any conditional rules, set selectedField to the field that the rule is on
    useEffect(() => {
        const fieldId = activePage?.conditionalRules?.[0]?.field?.id ?? null;
        const field = fieldId ? filteredFields.find((f) => f.id === fieldId) : null;

        const nextValue = field ? {id: field.id, label: field.label} : null;

        setSelectedFieldOption((prev) => {
            if (prev?.id === nextValue?.id) {
                return prev;
            }
            return nextValue;
        });
    }, [activePage?.id, activePage?.conditionalRules, filteredFields]);

    // Map the answer options to options for the select picker
    const filteredAnswerOptions =
        formSchema?.properties[selectedField?.attribute?.id ?? '']?.options ?? [];
    const answerOptions: SelectPickerOption[] = filteredAnswerOptions.map(
        (option: EnumFieldOption) => {
            return {
                id: option.id,
                label: option.name,
            };
        }
    );

    const ruleOptions: SelectPickerOption[] = useMemo(
        () => buildNavOptions(props.graphqlPages, props.activePageIndex),
        [props.graphqlPages, props.activePageIndex]
    );

    const updateRuleForAnswerOption = (fieldOptionId: string, inputRule: SelectPickerOption) => {
        const nextRuleByFieldOptionId = {
            ...ruleByFieldOptionId,
            [fieldOptionId]: inputRule,
        };
        setRuleByFieldOptionId(nextRuleByFieldOptionId);

        if (!activePage?.id || !selectedField?.id) {
            return;
        }

        const conditionalRules = answerOptions.map((answerOption) => {
            const foundRuleOption =
                nextRuleByFieldOptionId[answerOption.id] ??
                getRuleOptionForAnswerOption(answerOption);

            const selectedRuleOption = foundRuleOption ?? {
                id: PageRuleActionType.CONTINUE,
                label: 'Continue to next page',
            };

            const parsed = parseRuleOptionId(selectedRuleOption.id);
            const existingRule = activePage.conditionalRules?.find(
                (r) => r.enumElement?.id === answerOption.id
            );

            return {
                id: existingRule?.id ?? null,
                fieldId: selectedField.id,
                enumElementId: answerOption.id,
                actionType: parsed.actionType,
                targetPageId: parsed.targetPageId ?? null,
            };
        });

        updateFormPageRules({
            pageId: activePage.id,
            conditionalRules,
        });
    };

    const getRuleOptionForAnswerOption = (
        fieldOption: SelectPickerOption
    ): SelectPickerOption | null => {
        // Check local state first
        const overriddenRule = ruleByFieldOptionId[fieldOption.id];
        if (overriddenRule) {
            return overriddenRule;
        }

        return (
            ruleOptions.find(
                (ruleOption) => ruleOption.id === activePageConditionalRules[fieldOption.id]
            ) ?? null
        );
    };

    return (
        <div>
            <SidebarPanelHeader
                title='Branching Rules'
                onDone={() => props.setSidebarState('blocks-and-design')}
            />
            <div className='w-full pad-32 background-xlt-blue'>
                <Illustration src='form-field-branching' width='100%' />
            </div>
            <div className='pad-16 flex flex-dir-col gap-24'>
                {filteredFields.length > 0 ? (
                    <>
                        <Body size='small' color='light'>
                            Send users to different pages based on their answers.
                        </Body>
                        <Stack spacing={8}>
                            <Body size='small' isBold={true}>
                                Choose a field for branching logic
                            </Body>
                            <SelectPickerGeneric
                                options={fieldSelectorOptions}
                                valueKey='id'
                                labelKey='label'
                                isSearchable={true}
                                placeholder='Choose a field…'
                                value={selectedFieldOption ?? undefined}
                                onChange={(option: SelectPickerOption) => {
                                    setSelectedFieldOption(option);
                                }}
                            />
                        </Stack>
                        {answerOptions.length > 0 && (
                            <Stack spacing={8}>
                                <Body size='small' isBold={true}>
                                    Map each option to a destination page
                                </Body>
                                {answerOptions.map((option: SelectPickerOption) => (
                                    <BranchingRow
                                        key={option.id}
                                        fieldOption={option.label}
                                        rule={getRuleOptionForAnswerOption(option)}
                                        ruleOptions={ruleOptions}
                                        onRuleOptionChange={(rule: SelectPickerOption) => {
                                            updateRuleForAnswerOption(option.id, rule);
                                        }}
                                    />
                                ))}
                            </Stack>
                        )}
                    </>
                ) : (
                    <Body size='small' color='light'>
                        Add a dropdown or radio block to the form to start creating branching rules.
                    </Body>
                )}
            </div>
        </div>
    );
}
